<template>
<div class="form-group">
  <input v-if="isHidden" ref="input" :name="name" type="hidden" :value="value" />
  <template v-else>
    <div v-if="label && !isCheckbox && !isUrlBtn" :class="labelClass">
      <label class="control-label">
        <span v-html="$t(label)" :class="forceValidation && !isValid() ? 'invalid' : 'valid'"></span>
        &nbsp;
        <i v-if="required" class="color-orange fa fa-asterisk" aria-hidden="true"></i>
        <a v-if="tooltip" :data-balloon-length="tooltipLength" :data-balloon="$t(tooltip)" :data-balloon-pos="tooltipOrientation">
          <i class="fa fa-info-circle"></i>
        </a>
      </label>
      <p class="control-label-help" v-if="help" v-html="help"></p>
    </div>
    <div :class="`${fieldClass} ${forceValidation && !isValid() ? 'invalid' : 'valid'}`">
        <template v-if="isText || isUrlBtn">
          <a class="btn btn-primary" style="cursor: pointer" :href="value" target="_blank" v-if="isUrlBtn && disabled">{{$t(label)}}</a>
          <div class="input-group" v-else>
            <span class="input-group-addon" v-if="addon">{{ addon }}</span>
            <span class="input-group-addon" v-if="icon">
              <span :class="['fa', iconClass]"></span>
            </span>
            <a class="form-control" disabled style="cursor: pointer" :href="value" target="_blank" v-if="type === 'url' && disabled">{{value}}</a>
            <input v-else
                   ref="input"
                   :type="isUrlBtn ? 'url' : type"
                   :disabled="disabled"
                   class="form-control"
                   :name="name"
                   :aria-label="ariaLabel"
                   :value="value"
                   :maxlength="maxlength"
                   :min="minValue"
                   :max="maxValue"
                   v-on:keyup.enter="$emit('keyenter')"
                   v-on:input="updateValue($event.target.value)"
                   :placeholder="$t(placeholder)">
            <div class="suffix" v-if="suffix && suffixOffset !== '0px'" :style="`left: ${suffixOffset}`">{{$t(suffix)}}</div>
          </div>
        </template>

        <template v-else-if="isPasswordAdvanced">
          <div class="input-group tooltip-with-pre" :data-balloon="advancedPasswordComplexityTooltip" data-balloon-pos="left">
            <span class="input-group-addon" v-if="addon">{{ addon }}</span>
            <span class="input-group-addon" v-if="icon">
              <span :class="['fa', iconClass]"></span>
            </span>
            <input ref="input"
                   type="password"
                   :disabled="disabled"
                   class="form-control"
                   :name="name"
                   :aria-label="ariaLabel"
                   :value="value"
                   :maxlength="63"
                   v-on:keyup.enter="$emit('keyenter')"
                   v-on:input="updateValue($event.target.value)"
                   :placeholder="$t(placeholder)">
            <div v-show="value" :class="`postfix ${advancedPasswordComplexity}`">{{$t(advancedPasswordComplexityLabel)}}</div>
          </div>

          <ul v-show="value" :class="`complexity-bar ${advancedPasswordComplexity}`">
            <li></li>
            <li></li>
            <li></li>
            <li></li>
          </ul>
        </template>

        <textarea ref="input"
                  v-else-if="isMulti"
                  :disabled="disabled"
                  class="form-control"
                  :name="name"
                  :value="value"
                  v-on:input="updateValue($event.target.value)"></textarea>

        <template v-if="isDate">
            <datepicker
                    ref="input"
                    input-class="form-control"
                    :value="value"
                    :name="name"
                    :disabled="disabled"
                    :aria-label="ariaLabel"
                    :placeholder="$t(placeholder)"
                    :disabled-dates="disabledDates"
                    @input="updateValue"></datepicker>
        </template>

        <template v-else-if="isSelect">
          <dl class="dropdown" :disabled="disabled">
              <input ref="input" type="radio" :id="id" :value="value">
              <dt>
                <a ref="label" :for="id" :class="selectClass">
                  <span>{{ $t(selectValue) }}</span>
                </a>
              </dt>
          		<dd>
          			<ul>
                        <li v-if="!required && (!isPrintout || value == null) && !hasNullOption">
                            <a v-on:click="selectOption({}, null)" :class="value == null ? 'selected' : null">{{$t('Common.noSelection')}}</a>
                        </li>
          				<li v-for="(opt, index) in options" v-if="!isPrintout || isSelected(opt, index)">
                            <a v-on:click="selectOption(opt, index)" :class="isSelected(opt, index)" v-html="$t(opt.label)"></a>
                        </li>
          			</ul>
          		</dd>
          </dl>
        </template>

        <template v-else-if="isOption">
          <input ref="input" type="hidden" :value="value" />
          <div :class="['radio', {'radio-inline': inline}]" v-for="(opt, index) in options" v-if="!isPrintout || value === opt.value">
              <input type="radio"
                     :disabled="disabled"
                     :id="optionName(name, index)"
                     :name="name"
                     :data-value="opt.value"
                     :checked="value === opt.value"
                     v-on:click="() => updateValue(opt.value, opt.itemnum)">
              <label :for="optionName(name, index)">
                  <span v-html="$t(opt.label)"></span>
                  <a v-if="opt.tooltip" :data-balloon-length="opt.tooltipLength" :data-balloon="$t(opt.tooltip)" :data-balloon-pos="opt.tooltipOrientation">
                      <i class="fa fa-info-circle"></i>
                  </a>
              </label>
          </div>
        </template>

        <template v-else-if="isCheckbox">
          <label>
            <input ref="input"
                   type="checkbox"
                   :disabled="disabled"
                   :id="optionName(name, 0)"
                   :name="name"
                   :data-value="checkValue"
                   :value="value"
                   :checked="checked"
                   v-on:click="updateCheckbox">
              &nbsp;
              <label :for="optionName(name, 0)" v-html="$t(label)" :class="`${forceValidation && !isValid() ? 'invalid' : 'valid'}`"></label>
          </label>
        </template>

        <template v-else-if="isSwitch">
          <label class="switch-light switch-material" onclick="">
            <input ref="input"
                   type="checkbox"
                   :disabled="disabled"
                   :id="optionName(name, 0)"
                   :name="name"
                   :data-value="checkValue"
                   :value="value"
                   :checked="checked"
                   v-on:click="updateCheckbox">
            <span>
              <span>Off</span>
              <span>On</span>
              <a style="outline: none"></a>
            </span>
          </label>
        </template>

        <template v-else-if="isTiles">
          <input ref="input" type="hidden" :value="value" />
          <div
                  v-for="(opt, index) in options"
                  :class="['tile', {'tile-inline': inline}, isSelected(opt, index)]"
                  :style="!inline ? `width: ${Math.floor(100 / (cols || 1))}%` : ''"
                  v-if="!isPrintout || isSelected(opt, index)"
          >
            <label :for="optionName(name, index)" v-on:click="selectTile(opt, index)">
              <div>
                <img v-if="opt.imageUrl" :src="opt.imageUrl" :style="`max-height: ${height || '100px'}; max-width: ${width || 'initial'}`"/>
              </div>
              <div v-html="$t(opt.label)"></div>
            </label>
          </div>
        </template>

        <div class="error-message" v-if="error"><i class="fas fa-exclamation-triangle"></i> {{$t(error)}}</div>

        <slot></slot>
    </div>
  </template>
</div>
</template>
<script>
import Datepicker from 'vuejs-datepicker';
import {
  getPasswordComplexity,
  hasDigit,
  hasLower, hasSpecial,
  hasUpper,
  isStrongPassword,
} from '../lib/password'

export default {
  components: {
    Datepicker
  },
  data() {
    return {
      suffixOffset: 0
    }
  },
  props: {
    isPrintout: {
      type: Boolean,
      default: false,
    },
    addon: {
      type: String
    },
    height: String,
    width: String,
    icon: {
      type: String
    },
    required: {},
    defaultValue: {},
    checkValue: {
      default: true
    },
    group: {},
    value: {},
    maxlength: {},
    name: {
      type: String
    },
    type: {
      type: String,
      default: 'text'
    },
    help: {
      type: String
    },
    label: {
      type: String
    },
    ariaLabel: {
      type: String
    },
    itemnum: {
      type: String
    },
    inline: {
      type: Boolean,
      default: false
    },
    disabled: {},
    labelClass: {
      default: 'col-md-3 col-sm-5 col-xs-12'
    },
    fieldClass: {
      default: 'col-md-9 col-sm-7 col-xs-12'
    },
    placeholder: {
      type: String,
      default() {
        return 'Common.noSelection';
      }
    },
    options: {
      default() {
        return [];
      }
    },
    id: {
      type: String,
      default() {
        var text = "";
        var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

        for (var i = 0; i < 10; i++)
            text += possible.charAt(Math.floor(Math.random() * possible.length));

        return text;
      }
    },
    tooltip: {
      type: String
    },
    tooltipLength: {
      type: String,
      default: 'medium' // small, medium, large or fit.
    },
    tooltipOrientation: {
      type: String,
      default: 'up' // up, down, left or right
    },
    min: {},
    max: {},
    cols: { default: 1},
    suffix: {
      type: [String, Object],
    },
    forceValidation: {
      type: Boolean,
    },
    error: {
      type: [String, Object],
    },
  },
  computed: {
    minValue() {
      if (this.type === 'date' && typeof this.min === 'number') {
        const d = new Date();
        d.setHours(0);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        d.setDate(d.getDate() + this.min);
        return `${d.getFullYear()}-${('0' + (1 + d.getMonth())).substr(-2)}-${('0' + d.getDate()).substr(-2)}`;
      }

      return !this.isText ? undefined : this.min
    },
    maxValue() {
      if (this.type === 'date' && typeof this.max === 'number') {
        const d = new Date();
        d.setHours(0);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        d.setDate(d.getDate() + this.max);
        return `${d.getFullYear()}-${('0' + (1 + d.getMonth())).substr(-2)}-${('0' + d.getDate()).substr(-2)}`;
      }

      return !this.isText ? undefined : this.max
    },
    disabledDates() {
      return {
        to: this.minValue ? new Date(this.minValue) : undefined,
        from: this.maxValue ? new Date(this.maxValue) : undefined,
      };
    },
    hasNullOption() {
      return this.options.some(x => !x.value);
    },
    iconClass() {
      return 'fa-' + this.icon;
    },
    selectClass() {
      return this.value == null || this.value == '' ? 'default' : 'selected'
    },
    hasValue() {
      return !(typeof this.value == 'undefined' || this.value === null);
    },
    selectValue() {
      if (!this.hasValue) {
        return this.placeholder
      }

      if (typeof this.options === 'object') {
        for (let index in this.options) if (this.options.hasOwnProperty(index)) {
          const {value, label} = this.options[index];
          if (this.value === (value == null ? index : value)) {
            return label;
          }
        }
      }

      return this.value;
    },
    isText() {
      return (this.type === 'text' && this.options.length === 0)
        || this.type === 'password'
        // || this.type === 'date'
        || this.type === 'number'
        || this.type === 'url';
    },
    isPasswordAdvanced() {
      return this.type === 'password-advanced'
    },
    isDate() {
      return this.type === 'date'
    },
    isUrlBtn() {
      return this.type === 'url-btn';
    },
    isMulti() {
      return this.type === 'multi';
    },
    isHidden() {
      return this.type === 'hidden';
    },
    isOption() {
      if (this.type === 'checklist' || this.type === 'select' || this.type === 'tiles' || this.type === 'switch') {
        return false;
      }

      if (Array.isArray(this.options) && this.options.length > 0) {
        return true;
      }

      return typeof this.options === 'object' && Object.keys(this.options).length > 0;
    },
    isSelect() {
      return this.type === 'select';
    },
    isCheckbox() {
      return this.type === 'checkbox';
    },
    isSwitch() {
      return this.type === 'switch';
    },
    isTiles() {
      return this.type === 'tiles';
    },
    picked() {
      return this.value;
    },
    checked() {
      return this.value === this.checkValue;
    },
    advancedPasswordComplexity() {
      const x = getPasswordComplexity(this.value);
      return !this.value.length ? '' : x === 'none' ? 'low' : x;
    },
    advancedPasswordComplexityLabel() {
      return this.advancedPasswordComplexity ? ('Password.' + this.advancedPasswordComplexity) : '';
    },
    advancedPasswordComplexityTooltip() {
      const minLength = Math.max(6, this.$root.passwordMinLength || 6);
      const complexity = this.$root.passwordComplexity || 'weak';
      return [
        [x => x.length >= minLength, 'Password.lengthDesc', {x: minLength}],
        [hasLower, 'Password.lowerDesc'],
        complexity === 'strong' || complexity === 'medium' ? [hasUpper, 'Password.upperDesc'] : null,
        [hasDigit, 'Password.digitDesc'],
        complexity === 'strong' ? [hasSpecial, 'Password.specialDesc'] : null,
      ]
          .filter( x => x)
          .map(([p, l, ...rest]) => `${p(this.value || '') ? '✓' : '✘'} ${this.$t(l, ...rest)}`)
          .join('\n');
    },
  },
  mounted() {
    if (
      (typeof this.value == 'undefined')
      && (typeof this.defaultValue != 'undefined')
      && this.defaultValue != null
    ) {
      this.updateValue(this.defaultValue);
    }

    if (this.isSelect) {
      document.addEventListener('click', this.handleSelectClick);
    }

    this.updateSuffix(this.value);
  },
  beforeDestroy() {
    if (this.isSelect) {
      document.removeEventListener('click', this.handleSelectClick);
    }
  },
  methods: {
    isValid() {
      return (this.required !== true || (this.value != null && this.value !== '')) && this.isMinMet() && this.isMaxMet() && this.isStrongPassword();
    },
    isMinMet() {
      if (this.min == null) {
        return true;
      }

      if (this.type === 'number') {
        const value = parseInt(this.value, 10);
        return value >= this.min;
      }

      if (this.type === 'date') {
        const value = new Date(this.value);
        const d = new Date();
        d.setHours(0);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        d.setDate(d.getDate() + this.min);
        return value.getTime() >= d.getTime()
      }

      return true;
    },
    isMaxMet() {
      if (this.max == null) {
        return true;
      }

      if (this.type === 'number') {
        const value = parseInt(this.value, 10);
        return value <= this.max;
      }

      if (this.type === 'date') {
        const value = new Date(this.value).getTime();
        const d = new Date();
        d.setHours(0);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        d.setDate(d.getDate() + this.max + 1);
        return value <= d.getTime()
      }

      return true;
    },
    isStrongPassword() {
      if (!this.isPasswordAdvanced) {
        return true;
      }

      return isStrongPassword(this.value, this.$root.passwordComplexity, this.$root.passwordMinLength);
    },
    handleSelectClick(evt) {
      if (typeof this.$refs.label == 'undefined')
        return;

      if (evt.target == this.$refs.label || evt.target == this.$refs.input || evt.target == this.$refs.label.childNodes[0]) {
        this.$refs.input.checked = true;
        return;
      }

      this.$refs.input.checked = false;
    },
    isSelected(opt, index) {
      return this.value === (opt.value == null ? index : opt.value)  ? 'selected' : null;
    },
    updateCheckbox(evt) {
      if (this.disabled) {
        return ;
      }
      // this.$refs.input.checked = !this.$refs.input.checked;
      this.updateValue(this.$refs.input.checked ? this.checkValue : null, this.$refs.input.checked ? this.itemnum : null);
    },
    updateValue(value, itemnum) {
      if (this.disabled) {
        return ;
      }
      if (value instanceof Date) {
        value = value.toISOString().split('T')[0];
      }

      this.$refs.input.value = value;
      if (this.isCheckbox || this.isSwitch) {
        this.$refs.input.checked = value == this.checkValue
      }

      this.$forceUpdate();

      console.log('updateValue', value, itemnum, this.itemnum);
      this.$emit('input', value, this, value ? (itemnum || this.itemnum) : null);

      this.updateSuffix(value);
    },
    optionName(name, index) {
      return 'opt_' + name + '_ ' + index;
    },
    selectOption(opt, index) {
      if (this.disabled) {
        return ;
      }
      if (index === null) {
        this.updateValue(null, null);
      } else {
        this.updateValue(typeof opt.value === 'undefined' ? index : opt.value, opt.itemnum);
      }
      this.$refs.input.checked = false;
    },
    selectTile(opt, index) {
      if (this.disabled) {
        return ;
      }

      let value = typeof opt.value === 'undefined' ? index : opt.value;
      if (value === this.$refs.input.value && !this.required) {
        value = undefined;
      }
      this.updateValue(value, opt.itemnum);
    },
    updateSuffix(value) {
      const width = this.getTextWidth(value, '700 14px "Roboto", "Helvetica", "Arial", sans-serif');
      this.suffixOffset = (width > 0 ? width + 8 : 0) + 'px';
    },
    getTextWidth(text, font) {
      // re-use canvas object for better performance
      const canvas = this.getTextWidth.canvas || (this.getTextWidth.canvas = document.createElement("canvas"));
      const context = canvas.getContext("2d");
      context.font = font;
      const metrics = context.measureText(text);
      return metrics.width;
    }
  }
}
</script>
<style scoped>
    .tile.selected {
        background: #eee;
    }
    .tile-inline {
        display: inline-block;
    }
    .tile {
        text-align: center;
        cursor: pointer;
        padding: 8px;
        display: inline-block;
    }
    .tile * {
        cursor: pointer;
    }
    .input-group {
        position: relative;
    }
    .input-group .suffix {
        position: absolute;
        left: 0;
        top: 50%;
        color: #555;
        padding-left: 5px;
        font: inherit;
        bottom: 0;
        transform: translateY(-60%);
        opacity: .75;
    }

    span.invalid, label.invalid  {
        color: red;
    }

    .error-message {
        display: none;
        color: red;
        font-size: small;
        margin-top: -8px;
    }
    .invalid .error-message {
        display: block;
        font-weight: normal;
    }

    .invalid .form-control {
        border-color: red;
    }

    .complexity-bar {
      display: flex;
      list-style: none;
      flex-direction: row;
      width: 100%;
      height: 6px;
      flex: 1;
      margin: 8px 0 16px;
      padding: 0;
    }

    .complexity-bar li {
      display: flex;
      flex: 1;
      margin: 0 2px 0;
      background-color: #999;
      padding: 0;
    }

    .complexity-bar li:last-child {
      margin-right: 0;
    }

    .complexity-bar.low li:nth-child(5n+1) {
      background-color: red;
    }

    .complexity-bar.weak li:nth-child(5n+1), .complexity-bar.weak li:nth-child(5n+2) {
      background-color: orange;
    }

    .complexity-bar.medium li:nth-child(5n+1), .complexity-bar.medium li:nth-child(5n+2), .complexity-bar.medium li:nth-child(5n+3) {
      background-color: orange;
    }

    .complexity-bar.strong li:nth-child(5n+1), .complexity-bar.strong li:nth-child(5n+2), .complexity-bar.strong li:nth-child(5n+3), .complexity-bar.strong li:nth-child(5n+4) {
      background-color: green;
    }

    .complexity-bar.extra-strong li:nth-child(5n+1), .complexity-bar.extra-strong li:nth-child(5n+2), .complexity-bar.extra-strong li:nth-child(5n+3), .complexity-bar.extra-strong li:nth-child(5n+4) {
      background-color: green;
    }

    .postfix {
      position: absolute;
      top: 50%;
      right: 8px;
      transform: translateY(-50%);
    }

    .postfix.low {
      color: red
    }

    .postfix.medium, .postfix.weak {
      color: orange;
    }

    .postfix.strong, .postfix.extra-strong {
      color: green
    }

    .tooltip-with-pre::after {
      white-space: pre;
    }

</style>
